import {setAvatarId, setVoiceId} from "@/redux/slices/mlSlices/chatSlice";

import {useMemo} from "react";

import {ANAM_AVATAR_ID, ANAM_VOICE_ID} from "@hooks/AnamAvatar/utils";
import {useWebRTCSupport} from "@hooks/WebRTCSupport";
import warningImage from "@images/popups/error-upload.svg";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {WebRTCSupportPopup} from "@popups/simulationPopups/WebRTCSupportPopup";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {SmallDesc} from "@ui/descriptions/SmallDesc";
import {BorderArticle} from "@ui/general/BorderArticle";
import moment from "moment/min/moment-with-locales";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";

import styles from "./styles.module.scss";

export const PreviewCard = (props) => {
  const {
    name,
    image,
    due_date,
    finish_date,
    mandatory,
    mask,
    levelList,
    id,
    avatar_type,
    // trainingLimit,
    certified,
    isBig = false,
    useAnamChat = false,
  } = props;

  const dispatch = useDispatch();
  const {openPopup} = usePopup();
  const navigate = useNavigate();
  const {t} = useTranslation();
  const {isWebRTCSupported, errorMessage} = useWebRTCSupport();
  const {isMobile} = useSelector((state) => state.common.settings);

  const isMedical = window.location.host.includes("ava-medical.vivanti.eu");

  /** Anam test card: всегда уходим в чат с level easy для бэка */
  const easyScenarioLevel = useMemo(() => {
    if (!Array.isArray(levelList)) {
      return null;
    }
    return levelList.find((l) => l?.value === "easy") ?? levelList[0] ?? null;
  }, [levelList]);

  const isDueDateApproaching = (days) => {
    if (due_date || finish_date) {
      const now = moment();
      const dueDate = moment(due_date || finish_date);
      const diffDays = dueDate.diff(now, "days");

      return diffDays < days;
    }

    return false;
  };

  const isDueDatePassed = (date) => {
    if (!date) {
      return false;
    }

    const now = moment();
    const dueDate = moment(date);
    const diffDays = dueDate.diff(now, "days");

    return diffDays < -14;
  };

  const handleClickLevel = async (item, id) => {
    const {avatar_type} = item;

    /**
     * ------------------------------------------------
     * AVATAR_ID
     * b4fc2d60-3b82-4694-b243-93e9d2bb0242 - AVA Medical public (Anastasia in Grey Shirt)
     * cc634703-067c-48f1-a63e-5beee7286d37 - AVA Trainer paid (Лариса АВА Ру)
     * 3d12a519-c26e-438c-9b11-0434d8c7612b - AVA Trainer paid (Юрий Ашурков)
     * ------------------------------------------------
     * VOICE_ID
     * 04540e74-d1ae-418b-8a03-7f1ea939bace - AVA Medical (Оля.wav)
     * c1214c40-aa2d-4124-9ca9-ee71235a10d9 - AVA Trainer (Лариса)
     * 347c9a62-b4e3-4da4-97d4-0266a9a08d34 - AVA Trainer (Юрий Ашурков)
     * ------------------------------------------------
     */
    const medicalAvatarId = "b4fc2d60-3b82-4694-b243-93e9d2bb0242";
    const medicalVoiceId = "04540e74-d1ae-418b-8a03-7f1ea939bace";
    const trainerAvatarId =
      avatar_type === "male_60"
        ? "3d12a519-c26e-438c-9b11-0434d8c7612b"
        : "cc634703-067c-48f1-a63e-5beee7286d37";
    const trainerVoiceId =
      avatar_type === "male_60"
        ? "347c9a62-b4e3-4da4-97d4-0266a9a08d34"
        : "c1214c40-aa2d-4124-9ca9-ee71235a10d9";

    const chatState = {
      level: item,
      desc: {type: item.type, name, level: item.value},
      chatAvatarProvider: useAnamChat ? "anam" : "heygen",
    };

    const applyAvatarIdsAndGoChat = () => {
      if (useAnamChat) {
        dispatch(setAvatarId(ANAM_AVATAR_ID));
        dispatch(setVoiceId(ANAM_VOICE_ID));
      } else if (isMedical) {
        dispatch(setAvatarId(medicalAvatarId));
        dispatch(setVoiceId(medicalVoiceId));
      } else {
        dispatch(setAvatarId(trainerAvatarId));
        dispatch(setVoiceId(trainerVoiceId));
      }
      navigate(`/chat/${item.type}/${item.value}/${id}`, {
        state: chatState,
      });
    };

    if (!isWebRTCSupported) {
      openPopup(
        <WebRTCSupportPopup
          errorMessage={errorMessage}
          close={applyAvatarIdsAndGoChat}
        />,
      );
    } else {
      applyAvatarIdsAndGoChat();
    }
  };

  if (!isDueDatePassed(due_date || finish_date)) {
    return (
      <div className={"relative z-[1]"}>
        <BorderArticle>
          <div
            style={
              isBig
                ? {
                    width: !isMobile ? "550rem" : "",
                    maxWidth: !isMobile ? "" : "400rem",
                    height: !isMobile ? "300rem" : "200rem",
                  }
                : {}
            }
            className={
              isDueDateApproaching(0)
                ? `${styles.cardDisabled} ${styles.card}`
                : styles.card
            }
          >
            {image && (
              <img
                className={styles.cardPreview}
                src={image}
                alt={"preview-image"}
              />
            )}

            {mask && (
              <div
                className={styles.maskPreview}
                style={{background: `var(--${mask})`}}
              />
            )}

            <div className={styles.cardHeadWrapper}>
              {isDueDateApproaching(14) && (
                <div className={styles.cardHead}>
                  {(mandatory || finish_date) && (
                    <img
                      src={warningImage}
                      alt={"warning"}
                    />
                  )}

                  {(due_date || finish_date) && (
                    <SmallDesc>
                      <span>
                        {t("preview_card.date")}{" "}
                        <b>
                          {moment(due_date || finish_date).format("DD.MM.YYYY")}
                        </b>
                      </span>
                    </SmallDesc>
                  )}
                </div>
              )}

              {/* {trainingLimit && !isMobile && (
                <div className={styles.cardHeadInfo}>
                  <SmallDesc>
                    <span>{t("preview_card.available")}</span>
                    <b>
                      {isDueDateApproaching(0)
                        ? t("preview_card.available_false")
                        : t("preview_card.available_true")}
                    </b>
                  </SmallDesc>
                </div>
              )} */}

              {isBig && (
                <div className={styles.cardHeadInfo}>
                  <SmallDesc>
                    {/* <span>{t("preview_card.available")}</span> */}
                    <b>
                      {certified
                        ? t("page.dashboard.certification_status.passed")
                        : t("page.dashboard.certification_status.failed")}
                    </b>
                  </SmallDesc>
                </div>
              )}
            </div>

            {name && <h2 dangerouslySetInnerHTML={{__html: name}} />}

            {levelList && (
              <ul
                className={styles.cardFooter}
                style={{
                  width: isBig || useAnamChat ? "max-content" : "",
                  opacity: !certified ? 1 : 0.5,
                  ...(useAnamChat ? {justifyContent: "center"} : {}),
                }}
              >
                {useAnamChat && easyScenarioLevel != null ? (
                  <li
                    style={{
                      textAlign: "center",
                      pointerEvents: !certified ? "auto" : "none",
                    }}
                    key={"anam-start-easy"}
                    onClick={() =>
                      isDueDateApproaching(0) || certified
                        ? undefined
                        : handleClickLevel(
                            {
                              ...easyScenarioLevel,
                              avatar_type,
                            },
                            id,
                          )
                    }
                  >
                    <RootDesc>
                      <span>
                        <b>{t("button.start")}</b>
                      </span>
                    </RootDesc>
                  </li>
                ) : (
                  levelList.map((item) => (
                    <li
                      style={{
                        textAlign: isBig ? "center" : "",
                        pointerEvents: !certified ? "auto" : "none",
                      }}
                      key={item?.id}
                      onClick={() =>
                        isDueDateApproaching(0) || certified
                          ? undefined
                          : handleClickLevel(
                              {
                                ...item,
                                avatar_type,
                              },
                              id,
                            )
                      }
                    >
                      <RootDesc>
                        <span>
                          <b dangerouslySetInnerHTML={{__html: item?.name}} />
                        </span>
                      </RootDesc>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
        </BorderArticle>
      </div>
    );
  } else return null;
};
